// ============================================
// ALMACEN/PESTANAOFICINA.JS — consumibles de oficina (SUB-ALMACENES D, 2026-10-19)
// ============================================
// Lo que hay en Oficina (papel, cafe, toner... y lo que Central le haya traspasado), entradas directas a Oficina, traspaso
// desde Central y SALIDA A CONSUMO con quien y cuando. El consumo cuenta como gasto de oficina (no de proyecto): quien ve
// costos ve el gasto del mes. Articulos internos: alta propia; nunca en La Tienda.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuth } from '../../utils/sesion';
import { API_BASE } from '../../config/api';
import { fmtDinero } from '../../utils/dinero';
import { useUsuario } from '../../context/UsuarioContext';
import { Modal, C, PP, card, inputBase, labelStyle, btn, btnCancelar, btnOk, btnPrimario, Aviso, Foto, fmtCant, unidadCorta, fmtFechaHora } from './ui';
import ModalArticuloInterno from './ModalArticuloInterno';
import { ModalEntrada } from './PestanaInventario';
import Kardex from './Kardex';

export default function PestanaOficina({ esCelular }) {
  const { usuario, puede, accesoTotal, permisosFetched } = useUsuario();
  const veCentral = accesoTotal || (permisosFetched && (puede('almacen.inventario') || puede('almacen.compras')));
  const [datos, setDatos] = useState(null);
  const [articulos, setArticulos] = useState([]);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [modal, setModal] = useState(null); // {tipo:'entrada'} | {tipo:'articulo', a?} | {tipo:'consumo', x} | {tipo:'traspaso', x, a} | {tipo:'kardex'}
  const cargar = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([fetchAuth(`${API_BASE}/api/inventario/oficina`), fetchAuth(`${API_BASE}/api/inventario/articulos-internos?tipo=consumible`)]);
      const d1 = await r1.json().catch(() => ({})), d2 = await r2.json().catch(() => ({}));
      if (!r1.ok || !d1.ok) throw new Error(d1.error || 'No se pudo leer la Oficina');
      setDatos(d1); setArticulos(d2.ok ? d2.articulos || [] : []); setError('');
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const hecho = (msg) => { setModal(null); setAviso(msg); cargar(); setTimeout(() => setAviso(''), 5000); };

  const existencias = (datos && datos.existencias) || [];
  const consumos = (datos && datos.consumos) || [];
  const veCostos = !!(datos && datos.ve_costos);
  // Consumibles dados de alta que hoy no tienen nada en Oficina (para que se vea que existen y se pueda meter entrada).
  const sinExistencia = useMemo(() => articulos.filter(a => !existencias.some(x => x.tipo === 'articulo_interno' && String(x.referencia_id) === String(a.id))), [articulos, existencias]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: PP }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: C.grisClaro, lineHeight: 1.5, flex: 1, minWidth: 220 }}>
          <strong style={{ color: C.gris }}>🏢 Oficina</strong> · {existencias.length} artículo{existencias.length === 1 ? '' : 's'} con existencia
          {veCostos && datos ? <> · valor <strong style={{ color: C.oscuro }}>{fmtDinero(datos.costo_valor)}</strong> · gasto de oficina del mes <strong style={{ color: C.ambar }}>{fmtDinero(datos.costo_consumo_mes)}</strong> ({datos.consumos_mes} consumo{datos.consumos_mes === 1 ? '' : 's'})</> : null}
          <br />Consumibles que no se venden (papel, café, tóner…). Entran directo aquí o por traspaso desde Central. La <strong>salida a consumo</strong> dice quién y cuándo, y cuenta como gasto de oficina, no de proyecto.
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setModal({ tipo: 'articulo' })} style={btn(C.principal)}>➕ Nuevo consumible</button>
          <button onClick={() => setModal({ tipo: 'entrada' })} style={btnPrimario()}>📥 Entrada a Oficina</button>
        </div>
      </div>
      {aviso && <Aviso texto={aviso} />}
      {error && <Aviso tipo="error" texto={error} />}
      {!datos && !error && <div style={{ color: C.grisClaro, fontSize: 13 }}>Cargando…</div>}

      {datos && existencias.length === 0 && sinExistencia.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: 30, color: C.grisClaro, fontSize: 13 }}>Todavía no hay nada en Oficina. Da de alta un consumible y registra su entrada.</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
        {existencias.map(x => (
          <div key={x.sub_id} style={{ ...card, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Foto src={x.imagen_url} tam={48} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: C.gris, overflowWrap: 'anywhere' }}>{x.nombre}</div>
              <div style={{ fontSize: 11, color: C.grisClaro }}>{x.marca}{x.modelo ? ` · ${x.modelo}` : ''}{x.tipo !== 'articulo_interno' ? ' · del catálogo' : ''}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.oscuro, marginTop: 4 }}>{fmtCant(x.cantidad)} {unidadCorta(x.unidad)} <span style={{ fontSize: 11, color: C.grisClaro, fontWeight: 600 }}>en Oficina{Number(x.cantidad_central) > 0 ? ` · ${fmtCant(x.cantidad_central)} en Central` : ''}</span></div>
              {veCostos && <div style={{ fontSize: 11, color: C.grisClaro }}>a {fmtDinero(x.costo_unitario)} c/u · valor {fmtDinero(x.costo_total)}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setModal({ tipo: 'consumo', x })} style={btn(C.ambar, C.ambar, '#fff')}>Salida a consumo</button>
                {veCentral && <button onClick={() => setModal({ tipo: 'traspaso', x, de: 'oficina', a: 'central' })} style={btn()}>Regresar a Central</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {sinExistencia.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Consumibles dados de alta, sin existencia en Oficina</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sinExistencia.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: C.gris, flexWrap: 'wrap' }}>
                <Foto src={a.imagen_url} tam={28} /><span style={{ flex: 1, minWidth: 160 }}>{a.nombre}{Number(a.cantidad_central) > 0 ? <span style={{ color: C.grisClaro }}> · {fmtCant(a.cantidad_central)} en Central</span> : null}</span>
                <button onClick={() => setModal({ tipo: 'entrada', pre: a })} style={btn(C.principal)}>Entrada</button>
                {veCentral && Number(a.cantidad_central) > 0 && <button onClick={() => setModal({ tipo: 'traspaso', x: { existencia_id: a.existencia_id, nombre: a.nombre, unidad: a.unidad, cantidad: a.cantidad_central }, de: 'central', a: 'oficina' })} style={btn()}>Traer de Central</button>}
                <button onClick={() => setModal({ tipo: 'articulo', a })} style={btn()}>Editar</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {veCentral && (
        <div style={card}>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Traer de Central</div>
          <TraerDeCentral onElegir={(x) => setModal({ tipo: 'traspaso', x, de: 'central', a: 'oficina' })} />
        </div>
      )}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase' }}>Consumos recientes · quién y cuándo</div>
          <button onClick={() => setModal({ tipo: 'kardex' })} style={btn()}>Kardex completo de Oficina</button>
        </div>
        {consumos.length === 0 ? <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 6 }}>Sin consumos todavía.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {consumos.slice(0, 15).map(k => (
              <div key={k.id} style={{ fontSize: 12, color: C.gris, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: C.grisClaro, minWidth: 92 }}>{fmtFechaHora(k.creado_en)}</span>
                <span style={{ flex: 1 }}><strong>{fmtCant(k.cantidad)} {unidadCorta(k.unidad)}</strong> {k.producto_nombre} · consumió <strong style={{ color: C.ambar }}>{k.consumido_por_nombre || '—'}</strong>{k.quien ? <span style={{ color: C.grisClaro }}> · registró {k.quien}</span> : null}{veCostos && k.costo_total != null ? <span style={{ color: C.grisClaro }}> · {fmtDinero(k.costo_total)}</span> : null}{k.nota ? <span style={{ color: C.grisClaro, fontStyle: 'italic' }}> · {k.nota}</span> : null}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && modal.tipo === 'entrada' && <ModalEntrada existencias={[]} veCostos={veCostos} destino="oficina" soloInternos preseleccion={modal.pre ? { tipo: 'articulo_interno', referencia_id: String(modal.pre.id), nombre: modal.pre.nombre, marca: modal.pre.marca, modelo: modal.pre.modelo, imagen_url: modal.pre.imagen_url } : null} onCerrar={() => setModal(null)} onHecho={hecho} />}
      {modal && modal.tipo === 'articulo' && <ModalArticuloInterno tipo="consumible" articulo={modal.a} onCerrar={() => setModal(null)} onHecho={(a) => hecho(`${a.nombre} ${modal.a ? 'guardado' : 'dado de alta'}. Ahora registra su entrada a Oficina.`)} />}
      {modal && modal.tipo === 'consumo' && <ModalConsumo x={modal.x} usuario={usuario} onCerrar={() => setModal(null)} onHecho={hecho} />}
      {modal && modal.tipo === 'traspaso' && <ModalTraspaso x={modal.x} de={modal.de} a={modal.a} onCerrar={() => setModal(null)} onHecho={hecho} />}
      {modal && modal.tipo === 'kardex' && datos && <Modal titulo="Kardex de Oficina" onCerrar={() => setModal(null)} ancho={720}><div style={{ marginTop: 8 }}><Kardex filtro={{ subalmacen_id: datos.subalmacen.id }} titulo="" /></div></Modal>}
    </div>
  );
}

// Buscar en Central (lo que ya esta en el inventario) para traspasar a Oficina.
function TraerDeCentral({ onElegir }) {
  const [q, setQ] = useState('');
  const [lista, setLista] = useState([]);
  useEffect(() => {
    const t = q.trim(); if (t.length < 2) { setLista([]); return undefined; }
    let vivo = true;
    const h = setTimeout(() => {
      fetchAuth(`${API_BASE}/api/inventario`).then(r => r.json()).then(d => { if (!vivo || !d.ok) return; const tt = t.toLowerCase(); setLista((d.existencias || []).filter(e => Number(e.cantidad) > 0 && `${e.nombre} ${e.marca || ''} ${e.modelo || ''}`.toLowerCase().includes(tt)).slice(0, 8)); }).catch(() => {});
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q]);
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} style={inputBase} placeholder="Buscar en Central lo que quieres pasar a Oficina…" />
      {lista.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
        {lista.map(e => <button key={e.id} type="button" onClick={() => onElegir({ existencia_id: e.id, nombre: e.nombre, unidad: e.unidad, cantidad: e.cantidad })} style={{ display: 'flex', gap: 8, alignItems: 'center', textAlign: 'left', padding: '6px 8px', minHeight: 40, border: `1px solid ${C.borde}`, borderRadius: 8, background: '#fff', cursor: 'pointer', fontFamily: PP, fontSize: 12.5 }}><Foto src={e.imagen_url} tam={28} /><span style={{ flex: 1 }}>{e.nombre}</span><strong style={{ color: C.oscuro }}>{fmtCant(e.cantidad)} {unidadCorta(e.unidad)} en Central</strong></button>)}
      </div>}
    </div>
  );
}

function ModalConsumo({ x, usuario, onCerrar, onHecho }) {
  const [cantidad, setCantidad] = useState('1');
  const [quien, setQuien] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const cantOk = /^\d+(\.\d+)?$/.test(cantidad.trim()) && Number(cantidad) > 0 && Number(cantidad) <= Number(x.cantidad);
  const confirmar = async () => {
    if (!cantOk || enviando) return;
    setEnviando(true); setError('');
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario/traspasos`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ existencia_id: x.existencia_id, de: 'oficina', a: 'consumo', cantidad: cantidad.trim(), consumido_por_texto: quien.trim() || null, nota: nota.trim() || null }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'No se pudo registrar el consumo');
      onHecho(`Consumo registrado: ${fmtCant(cantidad)} ${unidadCorta(x.unidad)} de "${x.nombre}"${quien.trim() ? ` para ${quien.trim()}` : ''}.`);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setEnviando(false); }
  };
  return (
    <Modal titulo="Salida a consumo" onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={btnCancelar}>Cancelar</button>
      <button onClick={confirmar} disabled={!cantOk || enviando} style={btnOk(cantOk && !enviando)}>{enviando ? 'Guardando…' : 'Registrar consumo'}</button>
    </>}>
      <div style={{ fontSize: 12.5, color: C.gris, marginTop: 6 }}><strong>{x.nombre}</strong> · hay {fmtCant(x.cantidad)} {unidadCorta(x.unidad)} en Oficina</div>
      <label style={labelStyle}>Cantidad</label>
      <input value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputBase} inputMode="decimal" autoFocus />
      <label style={labelStyle}>¿Quién lo consume? (opcional: si se deja vacío, {usuario && usuario.nombre ? usuario.nombre : 'quien registra'})</label>
      <input value={quien} onChange={(e) => setQuien(e.target.value)} style={inputBase} placeholder="Nombre o área" maxLength={150} />
      <label style={labelStyle}>Nota (opcional)</label>
      <input value={nota} onChange={(e) => setNota(e.target.value)} style={inputBase} maxLength={500} />
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 8 }}>Cuenta como gasto de oficina, no de proyecto. Queda en el kardex con fecha y hora.</div>
      {error && <div style={{ marginTop: 10, background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>{error}</div>}
    </Modal>
  );
}

function ModalTraspaso({ x, de, a, onCerrar, onHecho }) {
  const [cantidad, setCantidad] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const tope = Number(x.cantidad || 0);
  const cantOk = /^\d+(\.\d+)?$/.test(cantidad.trim()) && Number(cantidad) > 0 && Number(cantidad) <= tope;
  const confirmar = async () => {
    if (!cantOk || enviando) return;
    setEnviando(true); setError('');
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario/traspasos`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ existencia_id: x.existencia_id, de, a, cantidad: cantidad.trim(), nota: nota.trim() || null }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'No se pudo traspasar');
      onHecho(`Traspaso hecho: ${fmtCant(cantidad)} ${unidadCorta(x.unidad)} de "${x.nombre}" de ${de === 'central' ? 'Central a Oficina' : 'Oficina a Central'}. El costo viaja con la pieza.`);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setEnviando(false); }
  };
  return (
    <Modal titulo={de === 'central' ? 'Traer de Central a Oficina' : 'Regresar de Oficina a Central'} onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={btnCancelar}>Cancelar</button>
      <button onClick={confirmar} disabled={!cantOk || enviando} style={btnOk(cantOk && !enviando)}>{enviando ? 'Guardando…' : 'Traspasar'}</button>
    </>}>
      <div style={{ fontSize: 12.5, color: C.gris, marginTop: 6 }}><strong>{x.nombre}</strong> · hay {fmtCant(tope)} {unidadCorta(x.unidad)} en {de === 'central' ? 'Central' : 'Oficina'}</div>
      <label style={labelStyle}>Cantidad (máx {fmtCant(tope)})</label>
      <input value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputBase} inputMode="decimal" autoFocus />
      <label style={labelStyle}>Nota (opcional)</label>
      <input value={nota} onChange={(e) => setNota(e.target.value)} style={inputBase} maxLength={500} />
      {error && <div style={{ marginTop: 10, background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>{error}</div>}
    </Modal>
  );
}
